import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'sonner';
import { BRAND_COLORS } from '../styles/theme';
import { useTranslation } from '../i18n';
import { useAuthV2 } from '../contexts/AuthContextV2';
import { formatDateForDisplay } from '../utils/dateUtils';
import {
  useUserProfileQuery,
  useUpdateProfileMutation,
  useAddCertificationMutation,
  useRemoveCertificationMutation,
} from './userProfile.queries';
import { useEditableField } from './userProfile/hooks/useEditableField';
import { useProfilePicture } from './userProfile/hooks/useProfilePicture';
import { PilotHoursCard } from './userProfile/PilotHoursCard';
import { BioSection } from './userProfile/BioSection';
import { CertificationsSection } from './userProfile/CertificationsSection';

export const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoading: authLoading } = useAuthV2();

  // --- Queries & mutations ---
  const profileQuery = useUserProfileQuery(authLoading ? undefined : userId);
  const profile = profileQuery.data;

  // Edit states (bio + name share the ref-preserving seed-on-fresh-data hook).
  const bioField = useEditableField(profile?.bio);
  const nameField = useEditableField(profile?.full_name);

  // Certification dialog
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [newCertName, setNewCertName] = useState('');
  const [newCertExpiry, setNewCertExpiry] = useState('');
  const [newCertDocument, setNewCertDocument] = useState<File | null>(null);

  const picture = useProfilePicture(userId, profile?.can_edit);

  const updateProfileMutation = useUpdateProfileMutation(userId ?? '');
  const addCertificationMutation = useAddCertificationMutation(userId ?? '');
  const removeCertificationMutation = useRemoveCertificationMutation(userId ?? '');

  const saving =
    updateProfileMutation.isPending ||
    picture.isPending ||
    addCertificationMutation.isPending ||
    removeCertificationMutation.isPending;

  const handleBack = () => {
    navigate(-1);
  };

  const handleSaveBio = () => {
    if (!userId || !profile) return;
    updateProfileMutation.mutate(
      { bio: bioField.value },
      {
        onSuccess: () => {
          bioField.stopEditing();
          toast.success(t('profile.bioSaved'));
        },
        onError: () => toast.error(t('profile.bioSaveError')),
      }
    );
  };

  const handleSaveName = () => {
    if (!userId || !profile) return;
    updateProfileMutation.mutate(
      { full_name: nameField.value },
      {
        onSuccess: () => {
          nameField.stopEditing();
          toast.success(t('profile.nameSaved'));
        },
        onError: () => toast.error(t('profile.nameSaveError')),
      }
    );
  };

  const handleAddCertification = () => {
    if (!userId || !newCertName.trim()) return;
    addCertificationMutation.mutate(
      {
        name: newCertName.trim(),
        expiry: newCertExpiry || undefined,
        document: newCertDocument || undefined,
      },
      {
        onSuccess: (result) => {
          if (result.success) {
            setCertDialogOpen(false);
            setNewCertName('');
            setNewCertExpiry('');
            setNewCertDocument(null);
            toast.success(t('profile.certificationAdded'));
          } else {
            toast.error(result.error || t('profile.certificationAddError'));
          }
        },
        onError: () => toast.error(t('profile.certificationAddError')),
      }
    );
  };

  const handleRemoveCertification = (index: number) => {
    if (!userId) return;
    removeCertificationMutation.mutate(index, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success(t('profile.certificationRemoved'));
        } else {
          toast.error(result.error || t('profile.certificationRemoveError'));
        }
      },
      onError: () => toast.error(t('profile.certificationRemoveError')),
    });
  };

  if (profileQuery.isLoading || authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (profileQuery.isError || !profile) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          p: 2,
        }}
      >
        <Typography color="error" sx={{ mb: 2 }}>
          {profileQuery.isError ? t('profile.loadError') : t('profile.notFound')}
        </Typography>
        <Button variant="contained" onClick={handleBack}>
          {t('common.goBack')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {profile.is_own_profile ? t('profile.myProfile') : t('profile.userProfile')}
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Profile Header Card */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            {/* Profile Picture */}
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={profile.profile_picture_url || undefined}
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: 48,
                  backgroundColor: BRAND_COLORS.forestGreen.main,
                  cursor: profile.can_edit ? 'pointer' : 'default',
                }}
                onClick={picture.handlePictureClick}
              >
                {profile.full_name?.[0] || profile.email[0].toUpperCase()}
              </Avatar>
              {profile.can_edit && (
                <IconButton
                  size="small"
                  onClick={picture.handlePictureClick}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: 'white',
                    boxShadow: 1,
                    '&:hover': { backgroundColor: '#f0f0f0' },
                  }}
                >
                  <CameraAltIcon fontSize="small" />
                </IconButton>
              )}
              <input
                type="file"
                ref={picture.fileInputRef}
                hidden
                accept="image/*"
                onChange={picture.handlePictureChange}
              />
            </Box>

            {/* Name, Email, Roles */}
            <Box sx={{ flex: 1 }}>
              {/* Name */}
              {nameField.isEditing && profile.can_edit ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField
                    value={nameField.value}
                    onChange={(e) => nameField.setValue(e.target.value)}
                    size="small"
                    placeholder={t('profile.fullName')}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveName}
                    disabled={saving}
                  >
                    {t('common.save')}
                  </Button>
                  <Button size="small" onClick={nameField.cancelEditing}>
                    {t('common.cancel')}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {profile.full_name || t('profile.noName')}
                  </Typography>
                  {profile.can_edit && (
                    <IconButton size="small" onClick={nameField.startEditing}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {profile.email}
              </Typography>

              {/* Roles */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {profile.roles.map((role) => (
                  <Chip
                    key={role}
                    label={t(`roles.${role}`)}
                    size="small"
                    sx={{
                      backgroundColor: BRAND_COLORS.forestGreen.main,
                      color: 'white',
                    }}
                  />
                ))}
              </Box>

              {/* Pilot Flight Hours */}
              <PilotHoursCard
                totalPilotHours={profile.total_pilot_hours}
                legacyFlightHours={profile.legacy_flight_hours}
              />

              {/* Delete picture button */}
              {profile.can_edit && profile.profile_picture_url && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={picture.handleDeletePicture}
                  sx={{ mt: 2 }}
                >
                  {t('profile.deletePicture')}
                </Button>
              )}
            </Box>
          </Box>

          {/* Member since */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('profile.memberSince')}: {formatDateForDisplay(profile.created_at)}
          </Typography>
        </Paper>

        {/* Bio Section */}
        <BioSection
          bio={profile.bio}
          canEdit={profile.can_edit}
          saving={saving}
          field={bioField}
          onSave={handleSaveBio}
        />

        {/* Certifications Section */}
        <CertificationsSection
          certifications={profile.certifications}
          canEdit={profile.can_edit}
          saving={saving}
          onRemove={handleRemoveCertification}
          dialogOpen={certDialogOpen}
          newCertName={newCertName}
          newCertExpiry={newCertExpiry}
          newCertDocument={newCertDocument}
          onOpenDialog={() => setCertDialogOpen(true)}
          onCloseDialog={() => setCertDialogOpen(false)}
          onNameChange={setNewCertName}
          onExpiryChange={setNewCertExpiry}
          onDocumentChange={setNewCertDocument}
          onAdd={handleAddCertification}
        />
      </Box>

      {/* Saving overlay */}
      {saving && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default UserProfilePage;
